import type { ProductionSiteReportingParameter, ReportingParameter, ReportingParameterValue } from '@/api/schemas/reports/common'
import { REPORTS } from '@/constants'
import { t } from '@/i18n'
import type { DataTableHeader } from '@/types/dataTable'
import { formatDate, parseDuration } from '@/utils/dateUtils'
import { capitalize, parseEmptyValue } from '@/utils/textUtils'
import type { ReportParameterRow } from '@/views/ReportsV2/types/parameters'

export interface ReportingParameterColumn {
    key: string
    label: string
}

type ReportingParameterFormatter = (value: string | number) => string

// Header keys of the columns that keep their own slot in the tasks tables.
export const REPORTING_PARAMETER_HEADER_KEYS = {
    TASK_NUMBER: 'taskNumber',
    MATERIAL_STATUS: 'materialStatus'
} as const

const { KEYS } = REPORTS.REPORTING_PARAMETERS

const MINUTE_IN_SECONDS = 60

const MATERIAL_STATUS_KEYS: readonly string[] = [KEYS.WAREHOUSE_STATUS, KEYS.CTP_STATUS, KEYS.RUBBER_STATUS]

// Plan values come raw from the production schedule, so the formatter is chosen by key.
const FORMATTERS: Record<string, ReportingParameterFormatter> = {
    [KEYS.TASK_START_TIME]: (value) => formatDate(value),
    [KEYS.TASK_DURATION]: (value) => parseDuration(Number(value) * MINUTE_IN_SECONDS)
}

export function reportingParameterLabel(parameter: Pick<ReportingParameter, 'key' | 'label'>): string {
    return parameter.label || capitalize(parameter.key)
}

export function formatReportingParameterValue(key: string, value: ReportingParameterValue | undefined): string {
    if (typeof value === 'boolean') {
        return value ? t('common.buttons.yes') : t('common.buttons.no')
    }

    if (value === null || value === undefined || value === '') {
        return String(parseEmptyValue(value))
    }

    const formatter = FORMATTERS[key]

    return formatter ? formatter(value) : String(value)
}

export function isMaterialStatusKey(key: string): boolean {
    return MATERIAL_STATUS_KEYS.includes(key)
}

export function buildReportingParameterColumns(configuration: ProductionSiteReportingParameter[]): ReportingParameterColumn[] {
    return [...configuration]
        .sort((first, second) => first.order - second.order)
        .map((item) => ({
            key: item.parameter.key,
            label: reportingParameterLabel(item.parameter)
        }))
}

export function findReportingParameter(
    parameters: ReportingParameter[] | undefined,
    key: string
): ReportingParameter | undefined {
    return parameters?.find((parameter) => parameter.key === key)
}

export function reportingParameterValue(parameters: ReportingParameter[] | undefined, key: string): string {
    return formatReportingParameterValue(key, findReportingParameter(parameters, key)?.value)
}

export function reportingColumnHeaderKey(key: string): string {
    return `reportingParameter:${key}`
}

export function pickMaterialStatusColumns(columns: ReportingParameterColumn[]): ReportingParameterColumn[] {
    return columns.filter((column) => isMaterialStatusKey(column.key))
}

/**
 * Columns rendered through the generic value slot. The order number and the material statuses
 * keep their own slots (order jobs preview, one merged status column).
 */
export function pickReportingValueColumns(columns: ReportingParameterColumn[]): ReportingParameterColumn[] {
    return columns.filter((column) => column.key !== KEYS.PROC_NUMBER && !isMaterialStatusKey(column.key))
}

/**
 * Table headers of the configured columns in their order. The material statuses share one column
 * at the position of the first of them.
 */
export function buildReportingParameterHeaders(
    columns: ReportingParameterColumn[],
    materialStatusTitle: string
): DataTableHeader[] {
    const headers: DataTableHeader[] = []
    let hasMaterialStatusHeader = false

    for (const column of columns) {
        if (isMaterialStatusKey(column.key)) {
            if (!hasMaterialStatusHeader) {
                headers.push({
                    title: materialStatusTitle,
                    key: REPORTING_PARAMETER_HEADER_KEYS.MATERIAL_STATUS,
                    sortable: false,
                    align: 'center'
                })
                hasMaterialStatusHeader = true
            }

            continue
        }

        if (column.key === KEYS.PROC_NUMBER) {
            headers.push({ title: column.label, key: REPORTING_PARAMETER_HEADER_KEYS.TASK_NUMBER, sortable: false })
            continue
        }

        headers.push({ title: column.label, key: reportingColumnHeaderKey(column.key), sortable: false, align: 'center' })
    }

    return headers
}

export function buildMaterialStatusTooltip(
    parameters: ReportingParameter[] | undefined,
    columns: ReportingParameterColumn[]
): string {
    return pickMaterialStatusColumns(columns)
        .map((column) => `${column.label}: ${reportingParameterValue(parameters, column.key)}`)
        .join('\n')
}

/**
 * Detail rows of the configured parameters. The order number comes from the schedule, so a task
 * reported outside of the plan gets `null` there and falls back to the number known to the report.
 */
export function buildReportingParameterRows(
    parameters: ReportingParameter[] | undefined,
    fallbackProcNumber: string | number | null | undefined
): ReportParameterRow[] {
    return (parameters ?? []).map((parameter) => {
        const value =
            parameter.key === KEYS.PROC_NUMBER && parameter.value === null ? (fallbackProcNumber ?? null) : parameter.value

        return {
            key: `reporting-parameter-${parameter.source}-${parameter.key}`,
            label: `${reportingParameterLabel(parameter)}:`,
            value: formatReportingParameterValue(parameter.key, value)
        }
    })
}
